
import { computed, defineComponent, getCurrentInstance, onActivated, onDeactivated, onMounted, PropType, Ref, ref } from 'vue';
import cookies from 'js-cookie';
import KwaiPlayer from '@ks-video/kwai-player-web/full';
import TopBar from './components/top-bar/index.vue';
import VideoCard from './components/video-card/index.vue';
import PictureCard from './components/picture-card/index.vue';
import ExpandWords from './components/expand-words/index.vue';
import List from './components/list/index.vue';
import LoginPopup from '@/components/login/index.vue';
import LogoutPopup from '@/components/logout-popup/index.vue';
import { commonPvParams, replacePath } from '~/common/utils';
import { getKwaiLog, immediateSendLog } from '~/common/radar';
import { useLogin } from '~/hooks/useLogin';
import { Feed } from '~/api/feed';
import { DiscoverApi, DiscoverItem } from '~/api/discover';
import { RequestSource, ResponseBaseResult, WebFeedType } from '~/api/base';
import { getDiscoverList } from './discoverUtil';
import { Picture } from '~/api/topic';

export default defineComponent({
    components: {
        TopBar,
        LoginPopup,
        LogoutPopup,
        VideoCard,
        PictureCard,
        List,
        ExpandWords,
    },
    props: {
        feedsListOrigin: {
            type: Array as PropType<Array<DiscoverItem>>,
            default: () => [],
        },
        cursor: {
            type: [Number, String],
            default: '',
        },
    },
    setup(props) {
        const { proxy } = getCurrentInstance() || {};
        const fmp_time = Date.now();
        const videoCategoryCardRefs = ref([] as any);
        const muted = ref(true);
        const topBarRef = ref<InstanceType<typeof TopBar>>();
        const loading = ref(false);
        const expandwordsRef = ref();
        const expandwords = ref(false);
        const fromUser = ref(false);
        const feeds = ref<Array<DiscoverItem>>(props.feedsListOrigin);
        const pcursor = ref<number | string>(props.cursor);
        const finished = computed(() => pcursor.value === 'no_more');
        const discoverCurrentIndex = ref(0);
        const currentPlayVideo = ref<KwaiPlayer>();

        const {
            showLoginPopup,
            showLogoutPopup,
            isLoginStatus,
            currentUserHeadUrl,
            handleLoginPopupClose,
            changeLoginStatus,
            handleLoginPopupOpenkwai,
            changeLoginPopupShow,
            handleLogout,
            handleLogoutCancel,
            handleLogoutPopupShow,
        } = useLogin();

        const setMuted = (type: boolean) => {
            muted.value = type;
        };

        const loadMorePost = async () => {
            if (loading.value || finished.value) {
                return;
            }
            loading.value = true;

            const pathname = window?.location?.pathname || '';
            const content = decodeURIComponent(pathname.split('/')?.[2] || '');
            const discoverRequest = {
                searchWord: encodeURIComponent(content?.trim()?.replace(/\s/g, '-')),
                pcursor: pcursor.value,
                count: 8,
                fromUser: fromUser.value,
                mobile: true,
                request_source: fromUser.value ? RequestSource.REQUEST_SOURCE_PWA_DIRECT_DISCOVER : RequestSource.REQUEST_SOURCE_PWA_SEO_DISCOVER,
                seoDefaultFilterV2: false,
            };
            const response = await getDiscoverList(discoverRequest, pathname || '', DiscoverApi, proxy?.$axios, proxy?.$store);
            if (response?.result === ResponseBaseResult.RESULT_OK && response?.feeds?.length) {
                feeds.value = [...feeds.value, ...response.feeds];
                pcursor.value = response?.pcursor || feeds?.value.length || 'no_more';
            } else {
                pcursor.value = 'no_more';
            }

            loading.value = false;
        };

        let isPageNotActive = true;
        const handleVisibilityChange = () => {
            if (document.hidden && isPageNotActive) {
                isPageNotActive = false;
                currentPlayVideo.value?.pause?.();
            } else {
                isPageNotActive = true;
                setTimeout(() => {
                    currentPlayVideo.value?.play?.();
                }, 50);
            }
        };

        onActivated(() => {
            if (!feeds?.value?.length) {
                loadMorePost();
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            setTimeout(() => {
                const themeColorMetaTag = document.querySelector('meta[name="theme-color"]');
                themeColorMetaTag?.setAttribute('content', '#fff');
            }, 500);
        });

        onDeactivated(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        });

        const sendPv = () => {
            const params = commonPvParams({ defaultPageSource: 'search' });
            // 获取启动config
            const country = proxy?.$store?.state?.startup?.countryInfo;
            const user_id = cookies.get('user_id');
            immediateSendLog({
                type: 'pv',
                name: 'SEO_RESEARCH_RESULT_PAGE',
                value: {
                    country,
                    user_id,
                    discoverUi: 'seo_new_discover',
                    search_word: topBarRef.value?.searchText || '',
                    ...params,
                },
            });
        };

        onMounted(async () => {
            const webLogger = getKwaiLog();
            try {
                webLogger?.plugins?.radar?.fmp(fmp_time);
            } catch (error) {
                console.error(error);
            }

            setTimeout(() => {
                sendPv();
            }, 0);

            if (proxy?.$isAppEnvironment) {
                setMuted(false);
            }

            expandwords.value = expandwordsRef.value?.showThis;

            (proxy as Record<string, any>).$bus.off('setMuted', setMuted);
            (proxy as Record<string, any>).$bus.off('changeLoginPopupShow', changeLoginPopupShow);

            (proxy as Record<string, any>).$bus.on('setMuted', setMuted);
            (proxy as Record<string, any>).$bus.on('changeLoginPopupShow', changeLoginPopupShow);
        });

        const onClickTopBarAvatarBtn = () => {
            if (isLoginStatus.value) {
                handleLogoutPopupShow(true);
                return;
            }
            changeLoginPopupShow(true);
        };

        const onTargetPhotoFocused = (index?: number) => {
            discoverCurrentIndex.value = index || 0;
            currentPlayVideo.value = videoCategoryCardRefs.value?.[discoverCurrentIndex.value]?.videoRef;
            for (let i = 0; i < videoCategoryCardRefs.value?.length; i++) {
                if (i !== index) {
                    videoCategoryCardRefs.value?.[i]?.videoRef?.pause?.();
                }
            }
        };

        const onClickSearchBtn = (searchText?: string) => {
            fromUser.value = true;
            replacePath(`/discover/${searchText ?? ''}`);
            feeds.value = [];
            pcursor.value = 0;
            loadMorePost();
        };

        const generateKey = (feed?: DiscoverItem, index?: number) => {
            if (feed?.web_feed_type === WebFeedType.PHOTO_CARD) {
                return `photo-${(feed as Feed)?.photo_id_str}-${index}`;
            }
            if (feed?.web_feed_type === WebFeedType.PICTURE_CARD) {
                return `picture-${(feed as Picture)?.pic_id_str}-${index}`;
            }
            return `feed-${index}`;
        };

        return {
            discoverCurrentIndex,
            currentUserHeadUrl,
            showLogoutPopup,
            showLoginPopup,
            isLoginStatus,
            topBarRef,
            finished,
            loading,
            feeds,
            muted,
            videoCategoryCardRefs,
            WebFeedType,
            expandwords,
            expandwordsRef,

            handleLoginPopupClose,
            changeLoginStatus,
            onClickTopBarAvatarBtn,
            handleLogout,
            handleLogoutCancel,
            handleLoginPopupOpenkwai,
            changeLoginPopupShow,
            loadMorePost,
            onTargetPhotoFocused,
            onClickSearchBtn,
            generateKey,
        };
    },
});
